import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getUserAvatarUrl, handleUserAvatarError, getTeamAvatarUrl, handleTeamAvatarError } from '../utils/avatars';
import Pagination from './Pagination';
import CountryDisplay from './CountryDisplay';
import ViolationsModal from './ViolationsModal';
import { getPowerCategory } from '../constants/powerGroups';
import './UsersList.css';
import './TeamMembersModal.css';

const EventParticipantsModal = ({ event, onClose }) => {
  const { user: currentUser } = useAuth();
  const { t, language } = useLanguage();
  const [participants, setParticipants] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total_pages: 1,
    total_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerTarget = useRef(null);
  const [error, setError] = useState(null);

  // Фильтры
  const [filters, setFilters] = useState({
    country: '',
    team_id: '',
    power_group_letter: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    teams: [],
    power_groups: []
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  // Журнал нарушений выбранного участника: открывается кликом по ФИО.
  // Флаг приходит с событием — журнал доступен админу и организаторам тура
  const [violationsRider, setViolationsRider] = useState(null);
  const canViewViolations = !!event.can_view_violations;
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const headerRefs = useRef({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const fetchParticipants = async (page = 1, currentFilters = null, append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    // Используем переданные фильтры или текущие из состояния
    const filtersToUse = currentFilters || filters;

    try {
      const params = new URLSearchParams();
      params.append('page', page);
      if (filtersToUse.country) params.append('country', filtersToUse.country);
      if (filtersToUse.team_id) params.append('team_id', filtersToUse.team_id);
      if (filtersToUse.power_group_letter) params.append('power_group_letter', filtersToUse.power_group_letter);

      const response = await axios.get(`/api/events/${event.id}/registrations?${params.toString()}`);

      // Получаем данные из нового формата ответа
      const registrationsData = response.data.registrations || [];
      const paginationData = response.data.pagination || null;

      // Обогащаем данные участников информацией о команде и группе мощности
      const enrichedParticipants = registrationsData.map(registration => ({
        ...registration.user,
        registration_type: registration.registration_type,
        power_group: registration.begin_data?.power_group,
        power_group_letter: mapPowerGroupToLetter(registration.begin_data?.power_group),
        ftp: registration.begin_data?.ftp,
        weight: registration.begin_data?.weight
      }));

      if (append) {
        setParticipants(prev => [...prev, ...enrichedParticipants]);
      } else {
        setParticipants(enrichedParticipants);
      }

      // Устанавливаем пагинацию
      if (paginationData) {
        setPagination(paginationData);
      } else {
        setPagination({
          current_page: 1,
          per_page: enrichedParticipants.length,
          total_pages: 1,
          total_count: enrichedParticipants.length
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`/api/events/${event.id}/registrations/filter_options`);
      setFilterOptions(response.data);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.filter-header') && !event.target.closest('.filter-dropdown-fixed')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  useEffect(() => {
    fetchParticipants(1, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.country, filters.team_id, filters.power_group_letter]);

  useEffect(() => {
    if (isMobile && observerTarget.current) {
      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && !isLoadingMore && pagination.current_page < pagination.total_pages) {
            fetchParticipants(pagination.current_page + 1, filters, true);
          }
        },
        { threshold: 0.1 }
      );

      observer.observe(observerTarget.current);
      return () => observer.disconnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isLoadingMore, pagination, filters]);

  const mapPowerGroupToLetter = getPowerCategory;

  const getRegistrationTypeLabel = (type) => {
    const types = {
      personal: t('events.registration_types.personal'),
      team: t('events.registration_types.team'),
      both: t('events.registration_types.both')
    };
    return types[type] || type;
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setOpenDropdown(null);
  };

  const toggleDropdown = (dropdownName) => {
    if (openDropdown === dropdownName) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(dropdownName);
      const headerElement = headerRefs.current[dropdownName];
      if (headerElement) {
        const rect = headerElement.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX
        });
      }
    }
  };

  const clearFilters = () => {
    setFilters({
      country: '',
      team_id: '',
      power_group_letter: ''
    });
  };

  const hasActiveFilters = filters.country || filters.team_id || filters.power_group_letter;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchParticipants(newPage, filters);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      onClose();
    }
  };

  const participantName = (participant) => (
    language !== 'ru' && participant.first_name_eng && participant.last_name_eng
      ? `${participant.first_name_eng} ${participant.last_name_eng}`
      : `${participant.first_name} ${participant.last_name}`
  );

  // ФИО открывает журнал нарушений участника — только тем, кому он доступен.
  // Остальные видят обычный текст, как и раньше
  const renderParticipantName = (participant) => {
    const name = participantName(participant);

    if (!canViewViolations) return name;

    return (
      <button
        type="button"
        className="participant-violations-link"
        title={t('violations.open_for_rider')}
        onClick={() => setViolationsRider({ id: participant.id, name })}
      >
        {name}
      </button>
    );
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content team-members-modal event-participants-modal">
        <div className="modal-header">
          <div className="team-info">
            {event.image ? (
              <img 
                src={event.image} 
                alt={event.name} 
                className="event-image-modal"
              />
            ) : (
              <div className="event-image-placeholder-modal">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </div>
            )}
            <h2>{event.name} - {t('events.participants')}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {loading && participants.length === 0 ? (
            <div className="loading">{t('common.loading')}</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <>
              <div className="members-stats">
                {t('events.total_participants')}: {pagination.total_count}
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="clear-filters-btn">
                    {t('users.clearFilters')}
                  </button>
                )}
              </div>

              {isMobile ? (
                <div className="members-cards-container">
                  {participants.map((participant) => (
                    <div key={participant.id} className="member-card">
                      <div className="member-card-header">
                        <img 
                          src={getUserAvatarUrl(participant.avatar)} 
                          alt={participant.nickname} 
                          className="member-card-avatar"
                          onError={handleUserAvatarError}
                        />
                        <div className="member-card-info">
                          <div className="member-card-name">
                            {renderParticipantName(participant)}
                          </div>
                          <div className="member-card-nickname">@{participant.nickname}</div>
                        </div>
                      </div>
                      
                      <div className="member-card-body">
                        <div className="member-card-row">
                          <CountryDisplay country={participant.country} language={language} />
                        </div>
                        
                        {participant.team && (
                          <div className="member-card-row">
                            <div className="team-cell">
                              <img 
                                src={getTeamAvatarUrl(participant.team.avatar, participant.team.name)} 
                                alt={participant.team.name} 
                                className="team-avatar-tiny"
                                onError={(e) => handleTeamAvatarError(e, participant.team.name)}
                              />
                              {/* Команды под событие в общем списке команд нет — ссылку не даем */}
                              {participant.team.event_team ? (
                                <span>{participant.team.name}</span>
                              ) : (
                                <a href={`/teams?search=${encodeURIComponent(participant.team.name)}`} className="team-link">
                                  {participant.team.name}
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="member-card-row">
                          <span className="power-group-cell">
                            {participant.power_group_letter ? (
                              <>
                                {participant.power_group_letter}
                                {participant.power_group && (
                                  <span className="power-group-value">
                                    ({parseFloat(participant.power_group).toFixed(2)} {t('users.powerUnit')})
                                  </span>
                                )}
                              </>
                            ) : (
                              '-'
                            )}
                          </span>
                        </div>
                        
                        <div className="member-card-row">
                          <span className={`registration-type-badge registration-type-${participant.registration_type}`}>
                            {getRegistrationTypeLabel(participant.registration_type)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {isLoadingMore && (
                    <div className="loading-more">{t('common.loading')}...</div>
                  )}
                  <div ref={observerTarget} className="observer-target"></div>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="users-table">
                  <thead>
                    <tr>
                      <th>{t('users.fullName')}</th>
                      <th>
                        <div 
                          className="filter-header" 
                          onClick={() => toggleDropdown('country')}
                          ref={el => headerRefs.current['country'] = el}
                        >
                          <span>{t('users.country')}</span>
                          <svg className={`filter-icon ${filters.country ? 'active' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </div>
                      </th>
                      <th>
                        <div 
                          className="filter-header" 
                          onClick={() => toggleDropdown('team')}
                          ref={el => headerRefs.current['team'] = el}
                        >
                          <span>{t('users.team')}</span>
                          <svg className={`filter-icon ${filters.team_id ? 'active' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </div>
                      </th>
                      <th>
                        <div 
                          className="filter-header" 
                          onClick={() => toggleDropdown('power_group')}
                          ref={el => headerRefs.current['power_group'] = el}
                        >
                          <span dangerouslySetInnerHTML={{ __html: t('users.powerGroup') }}></span>
                          <svg className={`filter-icon ${filters.power_group_letter ? 'active' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </div>
                      </th>
                      <th>{t('events.registration_type')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((participant) => (
                      <tr key={participant.id}>
                        <td>
                          <div className="user-name">
                            <img 
                              src={getUserAvatarUrl(participant.avatar)} 
                              alt={participant.nickname} 
                              className="user-avatar-small"
                              onError={handleUserAvatarError}
                            />
                            <div>
                              {renderParticipantName(participant)}
                              <div className="member-nickname">@{participant.nickname}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <CountryDisplay country={participant.country} language={language} />
                        </td>
                        <td>
                          {participant.team ? (
                            <div className="team-cell">
                              <img 
                                src={getTeamAvatarUrl(participant.team.avatar, participant.team.name)} 
                                alt={participant.team.name} 
                                className="team-avatar-tiny"
                                onError={(e) => handleTeamAvatarError(e, participant.team.name)}
                              />
                              <span>{participant.team.name}</span>
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="power-group-cell">
                          {participant.power_group_letter ? (
                            <>
                              {participant.power_group_letter}
                              {participant.power_group && (
                                <span className="power-group-value">
                                  ({parseFloat(participant.power_group).toFixed(2)} {t('users.powerUnit')})
                                </span>
                              )}
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          <span className={`registration-type-badge registration-type-${participant.registration_type}`}>
                            {getRegistrationTypeLabel(participant.registration_type)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}

              {!isMobile && pagination.total_pages > 1 && (
                <div className="participants-pagination-footer">
                  <Pagination pagination={pagination} onPageChange={handlePageChange} t={t} compact />
                </div>
              )}
            </>
                    )}        </div>
      </div>

      {openDropdown === 'country' && (
        <div 
          className="filter-dropdown-fixed"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
        >
          <div 
            className={`filter-dropdown-option ${!filters.country ? 'selected' : ''}`}
            onClick={() => handleFilterChange('country', '')}
          >
            {t('users.allCountries')}
          </div>
          {filterOptions.countries.map(country => (
            <div 
              key={country} 
              className={`filter-dropdown-option ${filters.country === country ? 'selected' : ''}`}
              onClick={() => handleFilterChange('country', country)}
            >
              <CountryDisplay country={country} language={language} />
            </div>
          ))}
        </div>
      )}

      {openDropdown === 'team' && (
        <div 
          className="filter-dropdown-fixed"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
        >
          <div 
            className={`filter-dropdown-option ${!filters.team_id ? 'selected' : ''}`}
            onClick={() => handleFilterChange('team_id', '')}
          >
            {t('users.allTeams')}
          </div>
          {filterOptions.teams.map(team => (
            <div 
              key={team.id} 
              className={`filter-dropdown-option ${filters.team_id === String(team.id) ? 'selected' : ''}`}
              onClick={() => handleFilterChange('team_id', String(team.id))}
            >
              {team.name}
            </div>
          ))}
        </div>
      )}

      {openDropdown === 'power_group' && (
        <div 
          className="filter-dropdown-fixed"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
        >
          <div 
            className={`filter-dropdown-option ${!filters.power_group_letter ? 'selected' : ''}`}
            onClick={() => handleFilterChange('power_group_letter', '')}
          >
            {t('users.allGroups')}
          </div>
          {filterOptions.power_groups.map(group => (
            <div 
              key={group} 
              className={`filter-dropdown-option ${filters.power_group_letter === group ? 'selected' : ''}`}
              onClick={() => handleFilterChange('power_group_letter', group)}
            >
              {t('users.group')} {group}
            </div>
          ))}
        </div>
      )}

      {violationsRider && (
        <ViolationsModal
          event={event}
          rider={violationsRider}
          onClose={() => setViolationsRider(null)}
        />
      )}
    </div>
  );
};

export default EventParticipantsModal;